import { reactive, ref } from "vue";
import { cloneDeep } from "lodash-es";
import eventHub from "@/helpers/utility/Events";
import { getRouteToFolder } from "@core/routes/routeDetector";

import type { Model } from "@/api/Model";
import type { Subset } from "@/helpers/utility/Type";
import type { ModelVersionWithData } from "@/api/ModelVersion";
import type { EditorAreaName } from "./editorAreas";

import { loadInfoAndData } from "@/api/SaveService";

const loadedModel = reactive({
  id: undefined as Model["id"] | undefined,
  name: "" as Model["name"],
  description: "" as Model["description"],

  created: "" as Model["created"],
  modified: "" as Model["modified"],

  type: undefined as undefined | Model["type"],

  // save location
  workspace: undefined as Model["workspace"] | undefined,
  folder: undefined as Model["folder"] | undefined,
  folderSlug: undefined as Model["folderSlug"],

  isLocked: false,
  isSimulationGame: false,

  permissions: {
    canFork: false,
    canUpdate: false,
    canDelete: false,
    canLock: false,
    canSwitchScenes: false
  } as {
    canFork?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
    canLock?: boolean;
    canSwitchScenes?: boolean;
  },

  lastVersion: undefined as Model["lastVersion"],

  accessableAreas: null as EditorAreaName[] | null,
  hiddenScenes: null as number[] | null,
  infoPopupsLocked: false,
  stakeholderId: null as number | null,

  shares: [],
  sharesCount: 0,
  publications: [],
  publicationsCount: 0,

  embedded: false,
  simulationLocked: false, // Can't close or switch the active simulation
  simulationControlsHidden: false // Can't advance the simulation themselves
});

const loadingError = ref<(Error & { status?: number; code?: string }) | null>(
  null
);

eventHub.on(
  "db-model-updated",
  (modelId: number | string, updatedModel: Model) => {
    if (loadedModel.id == modelId) {
      setLoadedModel(updatedModel);
    }
  }
);

eventHub.on(
  "db-modeldata-updated",
  (modelId: number | string, updatedModeldata: ModelVersionWithData) => {
    if (loadedModel.type != "workspace_model") return;
    if (loadedModel.id == modelId) {
      if (loadedModel.lastVersion == null) {
        loadedModel.lastVersion = { ...updatedModeldata };
      } else if (loadedModel.lastVersion.id != updatedModeldata.id) {
        loadInfoAndData(loadedModel as Model, updatedModeldata);
      }
    }
  }
);

/**
 * Replace the loaded model.
 */
function setLoadedModel(
  modelInfo: Partial<Model> & { accessableAreas?: EditorAreaName[] | null; hiddenScenes?: number[] | null; infoPopupsLocked?: boolean; stakeholderId?: number | null }
) {
  const oldId = loadedModel.id;

  loadedModel.id = modelInfo?.id;
  loadedModel.name = modelInfo?.name ?? "";
  loadedModel.description = modelInfo?.description ?? "";

  loadedModel.created = modelInfo.created ?? "";
  loadedModel.modified = modelInfo.modified ?? "";

  loadedModel.type = modelInfo?.type;

  loadedModel.workspace = modelInfo?.workspace;
  loadedModel.folder = modelInfo?.folder;
  loadedModel.folderSlug = modelInfo?.folderSlug ?? undefined;

  loadedModel.isLocked = modelInfo?.isLocked ?? false;
  loadedModel.isSimulationGame = modelInfo.isSimulationGame ?? false;

  loadedModel.lastVersion = modelInfo?.lastVersion ?? undefined;
  loadedModel.permissions = {
    canFork: modelInfo?.permissions?.canFork ?? false,
    canUpdate: modelInfo?.permissions?.canUpdate ?? false,
    canDelete: modelInfo?.permissions?.canDelete ?? false,
    canLock: modelInfo?.permissions?.canLock ?? false,
    canSwitchScenes: modelInfo?.permissions?.canSwitchScenes ?? false
  };

  loadedModel.accessableAreas = modelInfo?.accessableAreas ?? null;
  loadedModel.hiddenScenes = modelInfo?.hiddenScenes ?? null;
  loadedModel.infoPopupsLocked = modelInfo?.infoPopupsLocked ?? false;
  loadedModel.stakeholderId = modelInfo?.stakeholderId ?? null;

  loadedModel.embedded = false;
  loadedModel.simulationLocked = false;
  loadedModel.simulationControlsHidden = false;

  if (oldId != modelInfo.id) {
    eventHub.emit("model-id-updated", loadedModel.id, oldId);
  }

  setLoadingError(null);
  console.log("set loaded model", cloneDeep(loadedModel));
}

function setLoadingError(error: Error | null) {
  loadingError.value = error;
}

export function useLoadedModel() {
  /**
   * Restore the blank state
   */
  function resetLoadedModel() {
    loadedModel.id = undefined;
    loadedModel.name = "";
    loadedModel.description = "";

    loadedModel.created = "";
    loadedModel.modified = "";

    loadedModel.type = undefined;

    // save location
    loadedModel.workspace = undefined;
    loadedModel.folder = undefined;
    loadedModel.folderSlug = undefined;

    loadedModel.isLocked = false;
    loadedModel.isSimulationGame = false;

    loadedModel.permissions.canDelete = false;
    loadedModel.permissions.canUpdate = false;
    loadedModel.permissions.canFork = false;
    loadedModel.permissions.canLock = false;

    loadedModel.accessableAreas = null;
    loadedModel.hiddenScenes = null;
    loadedModel.infoPopupsLocked = false;
    loadedModel.stakeholderId = null;

    loadedModel.embedded = false;
    loadedModel.simulationLocked = false;
    loadedModel.simulationControlsHidden = false;

    setLoadingError(null);
  }

  /**
   * Update properties of the model and save them.
   */
  function updateLoadedModel(modelInfo: Subset<Model>) {
    const idIsChanged = "id" in modelInfo && modelInfo.id != loadedModel.id;
    const oldId = loadedModel.id;

    for (const k in modelInfo) {
      const key = k as keyof Model;
      if (key == "permissions") {
        if (modelInfo.permissions == null) {
          continue;
        }

        if (loadedModel.permissions == null) {
          loadedModel.permissions = {};
        }
        for (const perm in modelInfo.permissions) {
          const p = perm as keyof Model["permissions"];
          loadedModel.permissions[p] = modelInfo.permissions[p];
        }
        continue;
      } else if (key in loadedModel) {
        Object.assign(loadedModel, { [key]: modelInfo[key] });
      }
    }

    if (idIsChanged) {
      eventHub.emit("model-id-updated", loadedModel.id, oldId);
    }

    eventHub.emit("model-changed", loadedModel);
    console.log("model changed", loadedModel, modelInfo);

    return loadedModel as Model;
  }

  function getParentRoute(model = loadedModel) {
    switch (model.type) {
      case "workspace_model":
        if (model.workspace == null) {
          throw new Error("Can't get route to a model without workspace.");
        }
        if (model.folder == null && model.folderSlug == null) {
          throw new Error("Can't get route to a model without folder.");
        }
        return getRouteToFolder({
          workspace: model.workspace,
          id: model.folder,
          slug: model.folderSlug
        });

      case "shared_model":
        return {
          name: "shared-models"
        };

      case "public_model":
        return {
          name: "public-models"
        };

      default:
        return {
          name: "personal-workspace"
        };
    }
  }

  function setEmbedded(value: boolean) {
    loadedModel.embedded = value;
  }

  return {
    loadedModel,
    loadingError,

    resetLoadedModel,
    setLoadedModel,
    updateLoadedModel,

    setLoadingError,

    getParentRoute,

    setEmbedded
  };
}
